import { useState } from 'react';
import { usePlanetStore } from '../store/planetStore';
import { buildShipGroup, type PlanetActionOptions } from '../services/planet.service';
import { getShipIcon } from '../utils/shipIcons';
import { Button } from './ui/Button';
import { IconButton } from './ui/IconButton';
import { Icon } from './ui/Icon';
import { Chip } from './ui/Chip';
import {
    AUTO_DELIVERY_HOLD_HOURS,
    SHIP_GROUP_MAX_ITEMS,
    SHIP_GROUP_MIN_ITEMS,
    getShipDefinition,
    type PlayerRace,
    type ShipId,
} from '@kaeliux/shared';

interface ShipGroupBuilderProps {
    planetId: string;
    race: PlayerRace;
    cart: Partial<Record<ShipId, number>>;
    onRemove: (shipId: ShipId) => void;
    onQueued: () => void;
    options: PlanetActionOptions;
}

/**
 * Robots-only (issue #91): compose a linked batch of ship-build orders that
 * dock together — the shipyard's "+ Add to group" affordance on each
 * `ShipRow` feeds `cart` here. Delivery is held automatically for
 * AUTO_DELIVERY_HOLD_HOURS after the whole batch finishes construction, but
 * can be requested on demand any time before that once it's ready (see the
 * "Deliver Now" action in the Construction Queue).
 */
export default function ShipGroupBuilder({ planetId, race, cart, onRemove, onQueued, options }: ShipGroupBuilderProps) {
    const [isRequesting, setIsRequesting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const entries = (Object.entries(cart) as [string, number | undefined][])
        .filter((entry): entry is [string, number] => !!entry[1]);
    const canSubmit = entries.length >= SHIP_GROUP_MIN_ITEMS && entries.length <= SHIP_GROUP_MAX_ITEMS;

    async function handleQueueGroup() {
        if (!canSubmit) return;
        setIsRequesting(true);
        setError(null);
        try {
            const items = Object.fromEntries(entries) as Partial<Record<ShipId, number>>;
            const updated = await buildShipGroup(planetId, items, options);
            usePlanetStore.setState({ currentPlanet: updated });
            onQueued();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to queue group');
        } finally {
            setIsRequesting(false);
        }
    }

    return (
        <div data-testid="delivery-group-panel" className="rounded-panel border border-status-warning/80 bg-surface-panel p-4 flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
                <h2 className="text-title text-content-primary">Delivery Group</h2>
                <span className="text-micro text-content-muted">
                    {entries.length} / {SHIP_GROUP_MAX_ITEMS} items (min {SHIP_GROUP_MIN_ITEMS})
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {entries.map(([shipIdKey, quantity]) => {
                    const shipId = Number(shipIdKey) as ShipId;
                    const def = getShipDefinition(race, shipId);
                    return (
                        <div
                            key={shipIdKey}
                            className="flex items-center gap-2 rounded-chip border border-status-warning/40 bg-surface-void/40 px-2 py-1"
                        >
                            <img src={getShipIcon(race, shipId)} alt="" className="w-8 h-8 rounded-tile object-contain" />
                            <span className="text-label text-content-secondary">{def.name} × {quantity.toLocaleString()}</span>
                            <IconButton
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => onRemove(shipId)}
                                aria-label={`Remove ${def.name} from group`}
                            >
                                <Icon name="close" size={12} />
                            </IconButton>
                        </div>
                    );
                })}
            </div>
            <p className="text-micro text-content-muted">
                Ships dock together automatically {AUTO_DELIVERY_HOLD_HOURS}h after the group finishes
                building — or deliver them on demand as soon as they&apos;re ready.
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => { handleQueueGroup().catch(() => { }); }}
                    disabled={!canSubmit || isRequesting}
                >
                    {isRequesting ? 'Queuing…' : 'Queue Group'}
                </Button>
                {!canSubmit && !isRequesting && (
                    <span className="text-micro text-content-muted">
                        {entries.length < SHIP_GROUP_MIN_ITEMS
                            ? `Add at least ${SHIP_GROUP_MIN_ITEMS} ship types to the group`
                            : `Remove ${entries.length - SHIP_GROUP_MAX_ITEMS} — a group holds at most ${SHIP_GROUP_MAX_ITEMS} ship types`}
                    </span>
                )}
                {error && <Chip tone="danger">{error}</Chip>}
            </div>
        </div>
    );
}
